/**
 * Boot-time tenant resolution for internal (legacy-grant) users — the
 * decision TenantBootstrap applies when the caller's JWT carries
 * `tenant:<slug>` permissions.
 *
 * Pure, framework-free (house pattern, cf. organizationValidation.ts) so the
 * reload behavior is pinned by tests: a master who had switched into a
 * SELF-SERVICE tenant used to lose that selection on every hard reload —
 * boot restored their first legacy JWT grant instead, silently changing the
 * X-Tenant header, and the customers surface answered 409 ("only available
 * for self-service tenants") for a tenant the master never chose.
 */

import { LEGACY_TENANT_SLUGS } from '../api/types';

/**
 * Picks the tenant to mount for a user whose JWT grants legacy tenants.
 *
 * A saved SELF-SERVICE slug wins for masters: only the master-gated
 * TenantSwitcher can have written it, so it is the user's own last
 * selection, not another user's leftover. Non-masters (single-tenant legacy
 * users) never legitimately carry a self-service slug and keep their grant;
 * a saved LEGACY slug never reaches this decision (TenantBootstrap restores
 * it before consulting the JWT).
 */
export const resolveInternalBootTenant = (
    savedSlug: string | null,
    legacySlug: string,
    isMaster: boolean,
): string => {
    if (isMaster && savedSlug && !LEGACY_TENANT_SLUGS.has(savedSlug)) {
        return savedSlug;
    }
    return legacySlug;
};
